import React from 'react';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#0A192F] pt-24 pb-20">
      <SEO
        title="Privacy Policy"
        description="How Infotron Solutions collects, uses, and protects information from clients, candidates, and website visitors."
        path="/privacy"
      />
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
        <p className="text-gray-400 mb-12">Last updated: March 5, 2026</p>

        <div className="space-y-10 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
            <p className="leading-relaxed">
              Infotron Solutions ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you visit our website 
              or engage with our IT consulting, managed services, and talent solutions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
            <h3 className="text-lg font-semibold text-blue-500 mb-2">Personal Information</h3>
            <p className="leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide when you:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Fill out contact forms or request consultations</li>
              <li>Apply for job positions through our careers portal</li>
              <li>Subscribe to our newsletter or marketing communications</li>
              <li>Engage with us as a client or business partner</li>
            </ul>
            <p className="leading-relaxed mt-4">
              This information may include your name, email address, phone number, company name, job title, 
              and any other information you choose to provide.
            </p>

            <h3 className="text-lg font-semibold text-blue-500 mb-2 mt-6">Automatically Collected Information</h3>
            <p className="leading-relaxed">
              When you visit our website, we automatically collect certain information about your device, including 
              IP address, browser type, operating system, referring URLs, and information about how you interact 
              with our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Use of Information</h2>
            <p className="leading-relaxed mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Respond to your inquiries and provide requested services</li>
              <li>Process job applications and recruitment activities</li>
              <li>Send administrative information, updates, and marketing communications</li>
              <li>Improve our website, services, and user experience</li>
              <li>Analyze usage patterns and optimize our content</li>
              <li>Comply with legal obligations and protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Cookies and Tracking Technologies</h2>
            <p className="leading-relaxed mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website. 
              These technologies help us:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Remember your preferences and settings</li>
              <li>Understand how you navigate our website</li>
              <li>Analyze website traffic and performance</li>
              <li>Deliver relevant content and advertisements</li>
            </ul>
            <p className="leading-relaxed mt-4">
              You can control cookie preferences through your browser settings. However, disabling cookies may 
              affect certain functionality of our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Data Protection and Security</h2>
            <p className="leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information 
              against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, 
              secure servers, access controls, and regular security assessments. However, no method of transmission 
              over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Information Sharing</h2>
            <p className="leading-relaxed mb-4">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Service providers who assist in our operations (hosting, analytics, CRM)</li>
              <li>Professional advisors (lawyers, accountants, auditors)</li>
              <li>Business partners with your consent</li>
              <li>Authorities when required by law or to protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Third-Party Links</h2>
            <p className="leading-relaxed">
              Our website may contain links to third-party websites, including our job portal provider (CEIPAL), 
              LinkedIn, and other external services. We are not responsible for the privacy practices of these 
              external sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Data Retention</h2>
            <p className="leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in 
              this Privacy Policy, unless a longer retention period is required by law. For job applicants, we 
              typically retain application data for up to two years to consider you for future opportunities.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Your Rights</h2>
            <p className="leading-relaxed mb-4">Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Object to or restrict processing of your information</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="leading-relaxed mt-4">
              To exercise these rights, please contact us using the information provided below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. International Data Transfers</h2>
            <p className="leading-relaxed">
              As a global organization with offices in the USA, Canada, and India, your information may be 
              transferred to and processed in countries other than your country of residence. We ensure 
              appropriate safeguards are in place for such transfers in compliance with applicable data 
              protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage 
              you to review this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Contact Us</h2>
            <p className="leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-800/50 border border-blue-600/20 rounded-xl p-6">
              <p className="mb-2"><strong className="text-white">Infotron Solutions</strong></p>
              <p className="mb-2">Email: <a href="mailto:contact@infotronsolutions.com" className="text-blue-500 hover:text-blue-400">contact@infotronsolutions.com</a></p>
              <p className="mb-2">Phone: +1 (775) 305 9399</p>
              <p>Address: 3225 McLeod Dr. Ste 100, Las Vegas, NV 89121</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
